import { useState } from "react";
import {
  ApiKeysClient,
  PartiesClient,
  StreamsClient,
  type CreatedApiKey,
} from "../api/generated";
import { usePermissions } from "../auth/SessionContext";
import { Alert, Chip, Empty, Field, Panel, describeError, useAsync, when } from "../components/ui";

const keys = new ApiKeysClient("");
const parties = new PartiesClient("");
const streams = new StreamsClient("");

/**
 * Console module: <b>API keys</b>.
 *
 * A key is created against a (party, channel) subscription, because the key IS the target: the
 * caller never says who it is in a query string, which would be spoofable (§6.6). A store's POS
 * and its ordering client are two keys on the same party.
 */
export function ApiKeysPage() {
  const { hasCapability } = usePermissions();
  const canAdmin = hasCapability("principals:admin");

  const list = useAsync(() => keys.apiKeys_List());
  const partyList = useAsync(() => parties.parties_List(undefined, undefined));
  const streamList = useAsync(() => streams.streams_List());

  const [issued, setIssued] = useState<CreatedApiKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function revoke(principalId: string, label: string) {
    if (!window.confirm(`Revoke "${label}"? The credential stops working immediately.`)) return;
    setError(null);
    try {
      await keys.apiKeys_Revoke(principalId);
      await list.reload();
    } catch (e) {
      setError(describeError(e));
    }
  }

  async function rotate(principalId: string) {
    setError(null);
    try {
      setIssued(await keys.apiKeys_Rotate(principalId, { overlapHours: 24 }));
      await list.reload();
    } catch (e) {
      setError(describeError(e));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>API keys</h1>
          <p className="sub">
            Each key is bound to one party, channel, mode and stream. Rotating a key issues a
            replacement and keeps the old one working for an overlap window.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      {issued && (
        <Panel title="New key — copy it now">
          <Alert kind="warn">
            Copy this key now. The full value is not shown again.
          </Alert>
          <div className="mono-block">{issued.fullValue}</div>
          <div style={{ marginTop: 10 }}>
            <button className="ghost" onClick={() => setIssued(null)}>Done</button>
          </div>
        </Panel>
      )}

      {canAdmin && partyList.data && streamList.data && (
        <CreateKey
          parties={partyList.data.map((p) => ({ key: p.key!, name: p.name!, channels: p.channels ?? [] }))}
          streams={streamList.data.map((s) => s.key!)}
          onIssued={async (created) => { setIssued(created); await list.reload(); }}
        />
      )}

      <Panel title="Keys">
        {list.loading ? <Empty>Loading…</Empty> : (list.data ?? []).length === 0 ? (
          <Empty>No API keys yet.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Label</th><th>Party</th><th>Channel</th><th>Mode</th><th>Streams</th>
                <th>Key</th><th>Last used</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {list.data!.map((k) => (
                <tr key={k.id}>
                  <td>{k.label ?? k.principalName}</td>
                  <td className="mono">{k.partyKey}</td>
                  <td><Chip>{k.channel}</Chip></td>
                  <td>{k.mode}</td>
                  <td>{k.streams?.map((s) => <Chip key={s}>{s}</Chip>)}</td>
                  <td className="mono muted">{k.displayHint}</td>
                  <td className="muted">{when(k.lastUsedAt)}</td>
                  <td>
                    {k.revokedAt ? <Chip kind="crit">revoked</Chip>
                      : k.isActive ? <Chip kind="ok">active</Chip>
                      : <Chip kind="warn">expired</Chip>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {canAdmin && k.isActive && (
                      <>
                        <button className="ghost small" onClick={() => rotate(k.principalId!)}>Rotate</button>{" "}
                        <button className="danger small" onClick={() => revoke(k.principalId!, k.label ?? "")}>
                          Revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function CreateKey({ parties: partyOptions, streams: streamOptions, onIssued }: {
  parties: { key: string; name: string; channels: string[] }[];
  streams: string[];
  onIssued: (created: CreatedApiKey) => void;
}) {
  const [label, setLabel] = useState("");
  const [partyKey, setPartyKey] = useState(partyOptions[0]?.key ?? "");
  const [channel, setChannel] = useState(partyOptions[0]?.channels[0] ?? "pos");
  const [mode, setMode] = useState("inbox");
  const [stream, setStream] = useState(streamOptions[0] ?? "");
  const [canPublish, setCanPublish] = useState(false);
  const [scope, setScope] = useState("self");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const party = partyOptions.find((p) => p.key === partyKey);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      onIssued(await keys.apiKeys_Create({
        label,
        partyKey,
        channel,
        streams: [stream],
        mode,
        maxAudienceScope: scope,
        grants: [{ pattern: "*", publish: canPublish, subscribe: true }],
      }));
      setLabel("");
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Issue a key">
      {error && <Alert kind="error">{error}</Alert>}
      <div className="row">
        <Field label="Label"><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Store 118 POS" /></Field>

        <Field label="Party">
          <select value={partyKey} onChange={(e) => {
            setPartyKey(e.target.value);
            const next = partyOptions.find((p) => p.key === e.target.value);
            setChannel(next?.channels[0] ?? "pos");
          }}>
            {partyOptions.map((p) => <option key={p.key} value={p.key}>{p.name} ({p.key})</option>)}
          </select>
        </Field>

        <Field label="Channel">
          <select value={channel} onChange={(e) => setChannel(e.target.value)}>
            {(party?.channels ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>

        <Field label="Mode">
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="inbox">inbox — display client</option>
            <option value="cursor">cursor — processing client</option>
          </select>
        </Field>

        <Field label="Stream">
          <select value={stream} onChange={(e) => setStream(e.target.value)}>
            {streamOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <Field label="Audience scope">
          <select value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="self">self</option>
            <option value="direct">direct</option>
            <option value="group">group</option>
            <option value="tenant">tenant</option>
          </select>
        </Field>

        <div className="checkbox" style={{ paddingBottom: 8 }}>
          <input id="pub" type="checkbox" checked={canPublish} onChange={(e) => setCanPublish(e.target.checked)} />
          <label htmlFor="pub">may publish</label>
        </div>

        <button onClick={submit} disabled={busy || !label || !partyKey || !channel || !stream}>Issue key</button>
      </div>

      <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
        The party must already have this channel as a target. The key and its subscription are
        created together.
      </p>
    </Panel>
  );
}
